import { notAvailable } from 'core/common/constants';
import { NatalChartError, normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { NatalChartBlockNames } from 'core/content/entities';
import { updateNatalChartBlockValues } from 'core/content/store/actions';
import { getUserLocation } from 'core/user/store/selectors';
import { CreateNatalChartPayload } from '../../entities';
import { updateNatalChart } from './updateNatalChart';

export const createNatalChart =
  ({
    email,
    dateOfBirth,
    timeOfBirth,
    cityLocation,
    cityCoordinates,
  }: CreateNatalChartPayload): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      loggingService,
      natalChartService,
      contentService,
      observabilitySystem,
      funnelSettingsService,
      errorMonitoringService,
    } = context;

    const funnelName = funnelSettingsService.getFunnelName();
    const flowName = funnelSettingsService.getFlowName();
    const funnelLocale = funnelSettingsService.getFunnelLocale();

    const userLocation = getUserLocation(getState());

    const attributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: flowName,
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelLocale,
    };

    const natalChartSpan = observabilitySystem.startSpan(SpanName.CREATE_NATAL_CHART, {
      attributes,
    });

    const natalChartContentSpan = observabilitySystem.startSpan(SpanName.GET_NATAL_CHART_CONTENT, {
      attributes,
    });

    const dateTimeBirth = `${dateOfBirth}T${timeOfBirth}`;

    try {
      const [natalChartResult, natalChartContentResult] = await Promise.allSettled([
        natalChartService.createNatalChart({
          date_birth: dateTimeBirth,
          place_birth: cityLocation || 'Greenwich, Greater London, United Kingdom',
          lat: cityCoordinates[1] || 0.05,
          lng: cityCoordinates[0] || 51.45,
        }),
        contentService.getNatalChartBlockValues({
          block_names: NatalChartBlockNames,
          timezone: String(new Date(dateTimeBirth).getTimezoneOffset() * 60),
          date_time_birth: dateTimeBirth,
          place_birth: cityLocation || 'Greenwich, Greater London, United Kingdom',
          lat: cityCoordinates[1] || 0.05,
          lng: cityCoordinates[0] || 51.45,
        }),
      ]);

      const natalChart = natalChartResult.status === 'fulfilled' ? natalChartResult.value : null;
      const blockValues =
        natalChartContentResult.status === 'fulfilled' ? natalChartContentResult.value : null;

      if (natalChartResult.status === 'rejected') {
        const error = new NatalChartError('Failed to fetch natal chart data');

        loggingService.error(error);
        natalChartSpan.fail(error);
      }

      if (natalChartContentResult.status === 'rejected') {
        const error = new NatalChartError('Failed to fetch natal chart block values data');
        loggingService.error(error);
        natalChartContentSpan.fail(error);
      }

      if (natalChart) {
        dispatch(updateNatalChart(natalChart));
      }

      if (blockValues) {
        dispatch(updateNatalChartBlockValues(blockValues));
      }

      natalChartSpan.succeed();
      natalChartContentSpan.succeed();
    } catch (err) {
      const error = normalizeError(err);
      loggingService.error(error);

      errorMonitoringService.captureError(error, {
        tags: {
          email,
          funnel_name: funnelName,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'create_natal_chart',
          ...userLocation,
        },
      });

      natalChartSpan.fail(error);
      natalChartContentSpan.fail(error);
    }
  };
